import { mergeRequiredLanguageRows } from "@/utils/languageRequirements";

const cleanText = (text = "") => text.replace(/\s+/g, " ").trim();

/**
 * Languages form rows: every job-required language first in the job's order,
 * always with a blank proficiency the candidate fills in themselves (an
 * extracted level is never prefilled onto a required row), then any extra
 * extracted languages with their levels untouched. `mergeRequiredLanguageRows`
 * is the shared rule — the same one `requiredLanguageNames` locks, so the rows
 * and the locks cannot disagree. Which of those rows must carry a LEVEL is a
 * narrower question, answered by `levelledLanguageKeys`: every job language is
 * locked, but only the ones the job itself levelled demand an answer.
 *
 * `data.languages` is the /candidate-review shape (top-level, alongside a
 * duplicate copy inside draft_data). The apply page's SSE stream has no such
 * top-level key — it only ever has draft_data — so that's the fallback here,
 * or a resume's extracted languages silently never reach the picker on that
 * page even though the extraction itself ran and produced them.
 */
const buildLanguageRows = (data) =>
  mergeRequiredLanguageRows(
    data?.job_required_languages,
    Array.isArray(data?.languages) ? data.languages : data?.draft_data?.languages,
  );

// ─── Date normalizers ─────────────────────────────────────────────────────────

/**
 * Normalises any date the backend sends into "YYYY-MM".
 * This is the format stored in the form and sent back to the backend.
 *
 * Handles:
 *   "YYYY-MM"    → returned as-is                 (already correct)
 *   "MM-YYYY"    → flipped to "YYYY-MM"
 *   "MM/YYYY"    → converted to "YYYY-MM"          (legacy backend format)
 *   "YYYY-MM-DD" → strips the day part → "YYYY-MM"
 *   "" / null / undefined → returns ""
 */
const normalizeMonthYear = (value) => {
  if (!value) return "";

  // YYYY-MM  (primary — return as-is)
  if (/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) return value;

  // MM-YYYY  → YYYY-MM
  if (/^(0[1-9]|1[0-2])-\d{4}$/.test(value)) {
    const [month, year] = value.split("-");
    return `${year}-${month}`;
  }

  // MM/YYYY  → YYYY-MM
  if (/^(0[1-9]|1[0-2])\/\d{4}$/.test(value)) {
    const [month, year] = value.split("/");
    return `${year}-${month}`;
  }

  // YYYY-MM-DD  → YYYY-MM
  if (/^\d{4}-(0[1-9]|1[0-2])-\d{2}$/.test(value)) {
    return value.slice(0, 7);
  }

  // Unrecognised — pass through
  return value;
};

/**
 * Normalises education dates to year-only "YYYY" (or "Present").
 * Education schema uses YYYY, not MM-YYYY.
 */
const normalizeYearOnly = (value) => {
  if (!value) return "";
  if (value === "Present" || value === "present") return "Present";

  // Already YYYY
  if (/^\d{4}$/.test(value)) return value;

  // YYYY-MM or YYYY-MM-DD → extract year
  if (/^\d{4}-/.test(value)) return value.slice(0, 4);

  // MM/YYYY → extract year
  if (/^\d{2}\/\d{4}$/.test(value)) return value.split("/")[1];

  // MM-YYYY → extract year
  if (/^\d{2}-\d{4}$/.test(value)) return value.split("-")[1];

  return value;
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps API response data into the form-friendly structure.
 * @param {Object} data - API response
 * @returns {Object} - Form-ready data structure
 */
export const mapApiToForm = (data) => {
  const d = data.draft_data ?? {};
  const pi = d.personal_information ?? {};

  // ── Personal Information ──────────────────────────────────────────────────
  const personal_information = {
    name: pi.full_name ?? data.full_name ?? pi.name ?? "",
    email: pi.email ?? data.email ?? "",
    // Top-level on the response, not inside draft_data.personal_information —
    // it's the value submitted for THIS application, not the candidate's global one.
    alternate_email: data.alternate_email ?? "",
    phone: pi.phone ?? data.phone ?? "",
    location: pi.location ?? "",
    linkedin: pi.linkedin?.includes("linkedin") ? pi.linkedin : "",
    portfolio: pi.website ?? "",
    summary: cleanText(d.summary ?? ""),
  };

  // ── Skills ────────────────────────────────────────────────────────────────
  const skills = {
    list: Array.isArray(d.skills) ? d.skills : Array.isArray(d.skills?.list) ? d.skills.list : [],

    // Rows ({language, proficiency}) for the paired-dropdown editor —
    // job-required languages first, extras from the extraction after.
    languages: buildLanguageRows(data),
  };

  // ── Work Experience ───────────────────────────────────────────────────────
  const experience = Array.isArray(d.work_experience)
    ? d.work_experience.map((exp) => {
        const rawEnd = exp?.end_date ?? "";
        const isCurrent = exp?.is_current === true || rawEnd?.toLowerCase() === "present";

        return {
          jobTitle: exp?.title ?? "",
          company: exp?.company ?? "",
          startDate: normalizeMonthYear(exp?.start_date),
          endDate: isCurrent ? "present" : normalizeMonthYear(rawEnd),
          location: exp?.location ?? "",
          description: exp?.description ?? "",
        };
      })
    : [];

  // ── Education  (year-only dates) ──────────────────────────────────────────
  const education = Array.isArray(d.education)
    ? d.education.map((edu) => ({
        degree: edu?.degree ?? "",
        institution: edu?.institution ?? "",
        fieldOfStudy: edu?.field_of_study ?? "",
        startDate: normalizeYearOnly(edu?.start_date),
        endDate: normalizeYearOnly(edu?.end_date)?.toLowerCase() ?? "",
        grade: edu?.grade ?? "",
      }))
    : [];

  // ── Additional Sections ───────────────────────────────────────────────────
  const groupedSections = {};
  (d.additional_sections ?? []).forEach((sec) => {
    const title = sec.section_title ?? "Other";
    if (!groupedSections[title]) groupedSections[title] = [];
    groupedSections[title].push(sec.fields ?? {});
  });

  const normalizeFieldValue = (v) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "string") {
      const trimmed = v.trim();
      if (!trimmed || trimmed.toLowerCase() === "null") return "";
      return trimmed;
    }
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (typeof v === "object") {
      return Object.values(v)
        .filter((val) => val !== null && val !== undefined && val !== "")
        .join(" · ");
    }
    return String(v);
  };

  const additionalSections = Object.entries(groupedSections).map(([section_title, fieldsArr]) => ({
    section_title,
    items: fieldsArr.map((fields) => {
      const entry = Object.entries(fields)
        .map(([k, v]) => ({ key: k, value: normalizeFieldValue(v) }))
        .filter((field) => field.value !== "");

      // If extraction yielded no valid fields, seed one blank pair
      return entry.length > 0 ? entry : [{ key: "", value: "" }];
    }),
  }));

  // ── Social ─────────────────────
  const social = {
    instagram_url: data.instagram_url ?? "",
    facebook_url: data.facebook_url ?? "",
    twitter_url: data.twitter_url ?? "",
  };

  // ── Compensation ─────────────────
  const compensation = {
    current_salary: data.current_salary != null ? String(data.current_salary) : "",
    expected_salary: data.expected_salary != null ? String(data.expected_salary) : "",
    notice_period: data.notice_period ?? "",
    total_experience: data.total_experience != null ? String(data.total_experience) : "",
  };

  return {
    personal_information,
    skills,
    experience,
    education,
    additionalSections,
    social,
    compensation,
    currency: data.currency ?? "",
  currency_sign: data.currency_sign ?? "",
  };
};
